import { useCallback, useRef, useState } from 'react';
import { Controller, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { Grid } from '@alfalab/core-components/grid';
import { Link } from '@alfalab/core-components/link';
import { MaskedInput } from '@alfalab/core-components/masked-input';
import { useMatchMedia } from '@alfalab/core-components/mq';
import { Space } from '@alfalab/core-components/space';
import { Typography } from '@alfalab/core-components/typography';
import { isValidCardNumber } from '@alfalab/utils';
import { createCn } from 'bem-react-classname';
import config from 'config';
import { trackInAnalytics } from 'newclick-alfa-metrics';
import forge from 'node-forge';
import Reaptcha from 'reaptcha';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import { DesktopFormFooter } from '@components/DesktopFormFooter';
import { MobileFormFooter } from '@components/MobileFormFooter';
import { makeCardDataRequest } from '@store/requests/actions';
import { setIsReady, setLoading } from '@store/submit/actions';
import { validateExpDateInput } from '@utils/helpers';
import { getQueryParams } from '@utils/query';
import { router } from '@utils/router';
import { RootState } from '@utils/types';

import './CardInputForm.scss';

const MAX_TRANSACTION_SUM = 100000;
const MIN_TRANSACTION_SUM = 10;
const MINOR_UNITS = 100;

type FormValues = {
    cardNumber: string;
    cardExp: string;
    cvv: string;
    sum: string;
};

const encryptString = (string, publicKey) => {
    if (publicKey) {
        const pem = `-----BEGIN PUBLIC KEY----- ${publicKey} -----END PUBLIC KEY-----`;

        const key = forge.pki.publicKeyFromPem(pem);
        const encoded = key.encrypt(string);
        const str = forge.util.encode64(encoded);

        return str;
    }

    return null;
};

export const CardInputForm: React.FC = () => {
    const { publicKey } = useSelector((state: RootState) => state.request.initializeApplicationRequest.result);
    const [isVerified, setIsVerified] = useState<boolean>(false);
    const [transferLimitText] = useState<string>('Можно отправить от 10 ₽ до 100 тыс. ₽');
    const [titleForm] = useState<string>('Перевести с любой карты');
    const captcha = useRef(null);
    const [mobile] = useMatchMedia('--mobile');
    const dispatch = useDispatch();

    const { handleSubmit, control, reset } = useForm<FormValues>({ mode: 'onSubmit' });
    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        if (!isVerified) {
            captcha.current.renderExplicitly();
            dispatch(setIsReady(false));
        } else if (isVerified) {
            try {
                const { publicId }: any = getQueryParams();
                const encryptedCvv = encryptString(data.cvv, publicKey);
                const encryptedCardNumber = encryptString(data.cardNumber.replace(/\s/g, ''), publicKey);
                const captchaToken = await captcha.current.getResponse();

                if (encryptedCvv === null && encryptedCardNumber === null) {
                    router.internalRedirect('/error');

                    return;
                }

                dispatch(setLoading(true));
                reset({
                    cardNumber: '',
                    cardExp: '',
                    cvv: '',
                    sum: ''
                });

                dispatch(
                    makeCardDataRequest(
                        {
                            amount: {
                                minorUnits: MINOR_UNITS,
                                currency: 'RUR',
                                value: Math.round(+data.sum * MINOR_UNITS)
                            },
                            cardData: {
                                cardNumber: encryptedCardNumber,
                                cardExp: data.cardExp.replace('/', ''),
                                cvv: encryptedCvv
                            },
                            captchaToken
                        },
                        publicId
                    )
                );

                trackInAnalytics('SUBMIT_FORM', { result: 'Submit' });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
    };

    const onError: SubmitErrorHandler<FormValues> = () => {
        trackInAnalytics('SUBMIT_FORM', { result: 'Invalid' });
    };

    const onVerify = () => {
        setIsVerified(true);
        dispatch(setIsReady(true));
    };

    const onLoad = () => {
        dispatch(setIsReady(true));
    };

    const memoValidateExpDateInput = useCallback((value) => validateExpDateInput(value), []);

    const masks = {
        card: [
            /\d/,
            /\d/,
            /\d/,
            /\d/,
            ' ',
            /\d/,
            /\d/,
            /\d/,
            /\d/,
            ' ',
            /\d/,
            /\d/,
            /\d/,
            /\d/,
            ' ',
            /\d/,
            /\d/,
            /\d/,
            /\d/
        ],
        cvv: [/\d/, /\d/, /\d/],
        cardExp: [/[0-1]/, /\d/, '/', /\d/, /\d/],
        sum: createNumberMask({
            prefix: '',
            includeThousandsSeparator: false,
            allowDecimal: true,
            integerLimit: MAX_TRANSACTION_SUM.toString().length
        })
    };

    const placeholders = {
        card: '0000 0000 0000 0000',
        cvv: '000',
        cardExp: '00/00'
    };

    const cn = createCn('form');

    return (
        <form onSubmit={handleSubmit(onSubmit, onError)} className={cn()}>
            <Space fullWidth={true}>
                <Typography.TitleResponsive view='small' tag='div'>
                    {titleForm}
                </Typography.TitleResponsive>
                <Grid.Row>
                    <Grid.Col>
                        <Controller
                            control={control}
                            name='cardNumber'
                            rules={{
                                required: true,
                                minLength: 19,
                                validate: (value) => isValidCardNumber(value)
                            }}
                            render={({ field, fieldState }) => (
                                <MaskedInput
                                    autoFocus={true}
                                    mask={masks.card}
                                    placeholder={placeholders.card}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    value={field.value}
                                    maxLength={19}
                                    type='card'
                                    label='Номер карты'
                                    name={field.name}
                                    block={true}
                                    error={fieldState.invalid}
                                />
                            )}
                        />
                    </Grid.Col>
                </Grid.Row>
                <Grid.Row gutter={{ mobile: 16, tablet: 16, desktop: 16 }}>
                    <Grid.Col>
                        <Controller
                            control={control}
                            name='cardExp'
                            rules={{
                                required: true,
                                pattern: /(0[1-9]|1[0-2])\/[0-9]{2}/i,
                                validate: (value) => memoValidateExpDateInput(value)
                            }}
                            render={({ field, fieldState }) => (
                                <MaskedInput
                                    mask={masks.cardExp}
                                    placeholder={placeholders.cardExp}
                                    maxLength={5}
                                    type='text'
                                    label='Месяц/год'
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    value={field.value}
                                    name={field.name}
                                    block={true}
                                    error={fieldState.invalid}
                                />
                            )}
                        />
                    </Grid.Col>
                    <Grid.Col>
                        <Controller
                            control={control}
                            name='cvv'
                            rules={{ required: true, minLength: 3 }}
                            render={({ field, fieldState }) => (
                                <MaskedInput
                                    mask={masks.cvv}
                                    placeholder={placeholders.cvv}
                                    maxLength={3}
                                    type='password'
                                    label='СVC/CVV код'
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    value={field.value}
                                    name={field.name}
                                    block={true}
                                    error={fieldState.invalid}
                                />
                            )}
                        />
                    </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Col>
                        <Controller
                            control={control}
                            name='sum'
                            rules={{ required: true, max: MAX_TRANSACTION_SUM, min: MIN_TRANSACTION_SUM }}
                            render={({ field, fieldState }) => (
                                <MaskedInput
                                    mask={masks.sum}
                                    type='money'
                                    label='Сумма'
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    value={field.value}
                                    name={field.name}
                                    block={true}
                                    hint={transferLimitText}
                                    error={fieldState.invalid}
                                />
                            )}
                        />
                    </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Col>
                        <Typography.Text view='primary-small' color='secondary' tag='p'>
                            Обратите внимание, банк-эмитент карты может взимать комиссию за перевод.
                        </Typography.Text>
                        <Reaptcha
                            sitekey={config.get('client.siteKey')}
                            onVerify={onVerify}
                            onLoad={onLoad}
                            ref={captcha}
                            explicit={true}
                            size={mobile ? 'compact' : 'normal'}
                        />
                        <Typography.Text view='primary-small' color='secondary' tag='p'>
                            Продолжая, вы соглашаетесь с{' '}
                            <Link
                                href='https://alfa-mobile.alfabank.ru/mobile/s3/static/non-financial-services/non-financial/transfer-agreement.pdf'
                                target='_blank'
                            >
                                условиями переводов
                            </Link>
                            .
                        </Typography.Text>
                    </Grid.Col>
                </Grid.Row>
                {mobile ? <MobileFormFooter /> : <DesktopFormFooter />}
            </Space>
        </form>
    );
};
